/**
 * Bottom-Sheet mit Rastpunkten.
 *
 * ADR 0001 legt fest: Bottom-Sheets statt zentrierter Dialoge, ziehbar, mit
 * Rastpunkten. Der Grund ist der Daumen — ein zentrierter Dialog hat seine
 * Schaltflächen in der Bildschirmmitte und darüber, also außerhalb der bequemen
 * Reichweite bei einhändiger Bedienung.
 *
 * Unterbau ist Radix Dialog: Fokusfang, Rückgabe des Fokus beim Schließen,
 * Escape-Taste und `aria-modal` kommen dadurch mit (Kapitel 44). Ergänzt sind
 * hier nur das Ziehen und die Rastpunkte.
 */
import * as Dialog from "@radix-ui/react-dialog";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";

import { IconClose } from "@/components/icons";
import { t } from "@/i18n";
import { cn } from "@/lib/cn";

/** Rastpunkte als Anteil der Fensterhöhe, aufsteigend. */
const DEFAULT_SNAPS = [0.55, 0.92] as const;

/** Ab dieser Zugstrecke nach unten schließt das Sheet. */
const CLOSE_THRESHOLD_PX = 96;

export interface SheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** Wird unter dem Titel angezeigt und dient als Beschreibung für Screenreader. */
  description?: string;
  children: ReactNode;
  /** Anteile der Fensterhöhe, aufsteigend. Standard: 55 % und 92 %. */
  snapPoints?: readonly number[];
  /** Index des Rastpunkts beim Öffnen. */
  initialSnap?: number;
  /** Feste Leiste am unteren Rand, etwa für die Hauptaktion. */
  footer?: ReactNode;
}

export function Sheet({
  open,
  onOpenChange,
  title,
  description,
  children,
  snapPoints = DEFAULT_SNAPS,
  initialSnap = 0,
  footer,
}: SheetProps) {
  const [snapIndex, setSnapIndex] = useState(initialSnap);
  /** Zugstrecke während des Ziehens; 0 wenn nicht gezogen wird. */
  const [dragY, setDragY] = useState(0);
  const dragging = useRef<{ startY: number; pointerId: number } | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      setSnapIndex(initialSnap);
      setDragY(0);
    }
  }, [open, initialSnap]);

  const height = `${(snapPoints[snapIndex] ?? snapPoints[0] ?? 0.55) * 100}dvh`;

  const onPointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    // Nur der Griff löst das Ziehen aus. Würde die ganze Fläche ziehen, ließe
    // sich in einer Liste im Sheet nicht mehr blättern.
    dragging.current = { startY: event.clientY, pointerId: event.pointerId };
    event.currentTarget.setPointerCapture(event.pointerId);
  }, []);

  const onPointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const state = dragging.current;
    if (!state || state.pointerId !== event.pointerId) return;
    // Nur nach unten ziehbar; nach oben übernimmt der nächste Rastpunkt.
    setDragY(Math.max(0, event.clientY - state.startY));
  }, []);

  const onPointerUp = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const state = dragging.current;
      if (!state || state.pointerId !== event.pointerId) return;
      dragging.current = null;

      const travelled = event.clientY - state.startY;
      setDragY(0);

      if (travelled > CLOSE_THRESHOLD_PX) {
        // Weit nach unten gezogen: erst auf den niedrigeren Rastpunkt, von dort
        // aus schließen. Zwei Schritte statt einem, damit ein versehentliches
        // Wischen keine halb erfasste Meldung wegräumt.
        if (snapIndex > 0) setSnapIndex(snapIndex - 1);
        else onOpenChange(false);
        return;
      }
      if (travelled < -CLOSE_THRESHOLD_PX / 2 && snapIndex < snapPoints.length - 1) {
        setSnapIndex(snapIndex + 1);
      }
    },
    [snapIndex, snapPoints.length, onOpenChange],
  );

  const cycleSnap = useCallback(() => {
    setSnapIndex((current) => (current + 1) % snapPoints.length);
  }, [snapPoints.length]);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/45" />
        <Dialog.Content
          ref={panelRef}
          aria-describedby={description !== undefined ? "sheet-description" : undefined}
          style={{
            height,
            transform: dragY > 0 ? `translateY(${dragY}px)` : undefined,
            transition: dragging.current ? "none" : "height 180ms ease, transform 180ms ease",
            paddingBottom: "var(--safe-b)",
          }}
          className={cn(
            "fixed inset-x-0 bottom-0 z-50 flex flex-col",
            "rounded-sheet border-t border-line bg-surface shadow-sheet",
            "focus:outline-none",
          )}
        >
          {/* Griff: gleichzeitig Schaltfläche, damit der Rastpunkt auch per
              Tastatur und Screenreader gewechselt werden kann — eine Geste
              allein wäre nach Kapitel 44 unzureichend. */}
          <div
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            className="flex shrink-0 cursor-grab touch-none justify-center active:cursor-grabbing"
          >
            <button
              type="button"
              onClick={cycleSnap}
              aria-label={`${title} — Höhe wechseln`}
              className="flex min-h-touch w-24 items-center justify-center"
            >
              <span className="h-1.5 w-12 rounded-full bg-line-strong" />
            </button>
          </div>

          <header className="flex shrink-0 items-start gap-3 border-b border-line px-4 pb-3">
            <div className="min-w-0 flex-1">
              <Dialog.Title className="text-[1.125rem] font-semibold text-ink">{title}</Dialog.Title>
              {description !== undefined && (
                <Dialog.Description id="sheet-description" className="mt-0.5 text-[0.875rem] text-ink-muted">
                  {description}
                </Dialog.Description>
              )}
            </div>
            <Dialog.Close
              aria-label={t.common.close}
              className="-mt-1 -mr-1 flex size-touch shrink-0 items-center justify-center rounded-card text-ink-muted hover:bg-surface-sunken"
            >
              <IconClose />
            </Dialog.Close>
          </header>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4">{children}</div>

          {footer !== undefined && (
            <div className="shrink-0 border-t border-line bg-surface-raised px-4 py-3">{footer}</div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
